import { Injectable } from '@angular/core';

import { DataTableOptions } from '@services/table/table.service';
import { environment } from '@env';

import { v4 as uuidv4 } from 'uuid';
import { cloneDeep, find, get, has, includes, indexOf, isNull, isNumber, isString, map, orderBy, replace, toLower, trim } from 'lodash';

@Injectable({
  providedIn: 'root'
})
export class DataTableNavigationService {

  public currentIdsList: string[] = [];
  public lastActiveUrl: string;

  constructor() {
  }

  public registerCustomSortProvider(dataTableOptions: DataTableOptions): void {
    const customSortKey: string = uuidv4();

    ($.fn.dataTable.ext as any).afnSortData[customSortKey] = (settings) => {
      let dataItems = map(cloneDeep(settings.aoData), (dataItem) => {
        const data = dataItem._aData;
        data.idx = dataItem.idx;

        if (settings?.oInit?.rowvaluefrom) {
          data.dataMid = dataItem._aData[settings.oInit.rowvaluefrom];
        } else if (dataItem.nTr) {
          data.dataMid = dataItem.nTr.getAttribute('data-mid');
        }

        return data;
      });

      dataItems = this.sortTableData(dataItems, settings.aaSorting, dataTableOptions.columns);
      settings.aiDisplayMaster = map(dataItems, 'idx');
      this.currentIdsList = map(dataItems, 'dataMid');

      return settings;
    };

    this.addCustomSortField(dataTableOptions, customSortKey);
  }

  public getCachedOrder(tableId: string, pageUrl: string): any[] {
    let baseHref: string = environment.baseHref;

    if (includes(baseHref, '/')) {
      baseHref = replace(baseHref, /\//g, '');
      if (baseHref) {
        baseHref += '/';
      }
    }

    return get(JSON.parse(localStorage.getItem(`DataTables_${tableId}_/${baseHref}${pageUrl}`)), 'order', []);
  }

  public sortTableData(dataItems: any[], order: any[], columns?: DataTables.ColumnSettings[]) {
    if (order.length && dataItems.length) {
      if (columns?.length) {
        dataItems = orderBy(dataItems, this.getOrderFieldsByColumns(dataItems, order, columns), this.getOrder(order));
      } else {
        dataItems = orderBy(dataItems, this.getOrderFields(order), this.getOrder(order));
      }
    }

    return dataItems;
  }

  public getNextId(currentId: number | string, ids: number[] | string[]): number | string {
    const index = indexOf(ids, currentId);
    return index === ids.length - 1 ? null : ids[index + 1];
  }

  public getPreviousId(currentId: number | string, ids: number[] | string[]): number | string {
    const index = indexOf(ids, currentId);
    return index === 0 ? null : ids[index - 1];
  }

  public getOrderFieldsByColumns(dataItems: any[], order: any[], columns: DataTables.ColumnSettings[]): any[] {
    const sorts: any[] = [];
    const isDataTableColumn = this.isDataTableColumn(columns[0]);
    const sortKeyMap = {
      fieldValue: isDataTableColumn ? 'data' : 'id',
      fieldValueRender: isDataTableColumn ? 'render' : 'func',
    };

    map(order, (orderItem: any) => {
      const sortField: any = get(columns[orderItem[0]], sortKeyMap.fieldValue);
      const sortFieldRender: any = get(columns[orderItem[0]], sortKeyMap.fieldValueRender);
      const cellVal: (key, value) => string | number = get(columns[orderItem[0]], 'cellval');

      if (isString(sortField)) {
        const dataItem: any = find(dataItems, `${sortField}`);

        if (isString(get(dataItem, sortField)) || isNumber(get(dataItem, sortField))) {
          sorts.push((item) => this.getOrderItemValue(item[sortField]));
        } else if (sortFieldRender) {
          sorts.push((item) => this.getOrderItemValue(sortFieldRender(item[sortField], isDataTableColumn ? 'sort' : item)));
        } else if (cellVal) {
          if (isString(cellVal)) {
            sorts.push((item) => this.getOrderItemValue(item[cellVal]));
          } else {
            sorts.push((item) => this.getOrderItemValue(cellVal(item[sortField], item)));
          }
        }
      } else {
        const dataItem = find(dataItems, (dataItem) => sortField(dataItem));
        const data = sortField(dataItem);

        if (isString(data) || isNumber(data)) {
          sorts.push((item) => this.getOrderItemValue(sortField(item)));
        } else {
          sorts.push((item) => this.getOrderItemValue(sortFieldRender(item, isDataTableColumn ? 'sort' : item)));
        }
      }
    });

    return sorts;
  }

  public getOrder(order): any[] {
    return map(order, (orderItem) => orderItem[1]);
  }

  public getOrderItemValue(value: any): any {
    value = isNumber(value) ? value : trim(value);

    if (isString(value)) {
      return toLower(value);
    }

    if (isNull(value)) {
      return -1;
    }

    return value;
  }

  private addCustomSortField(dataTableOptions: DataTableOptions, customSortKey: string): void {
    const customSortField = {orderDataType: customSortKey, targets: '_all'};
    const existingCustomSortField = find(dataTableOptions.columnDefs, 'orderDataType');

    if (!dataTableOptions.columnDefs) {
      dataTableOptions.columnDefs = [];
    }

    if (existingCustomSortField?.orderDataType) {
      existingCustomSortField.orderDataType = customSortKey;
    } else {
      dataTableOptions.columnDefs.push(customSortField);
    }
  }

  private isDataTableColumn(column: DataTables.ColumnSettings): boolean {
    return has(column, 'data');
  }

  private getOrderFields(order) {
    return map(order, (orderItem) => (item) => {
      const itemValue = item[orderItem[0]];
      const orderValue = get(itemValue, '@data-order') || itemValue;

      return isFinite(+orderValue) ? +orderValue : orderValue;
    });
  }

}
